import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig, SortDirections } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { sortByDate } from "@app/shared/sortByDate";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import ProjectService from "@app/projects/projectService";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { getMostRecentSaturday, getMostRecentWednesday } from "@app/shared/utility/getMostRecentDay";
import { WeekendRunProjectStatusesResult } from "@app/operations/jobStatisticsResult";
import OperationsService from "@app/operations/operationsService";
import { ProjectRunConfigurationStatus } from "@app/projects/enumerators";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
interface WeekendRunProjectStatusesProps {
  clients: { clientId: number; name: string }[];
  clientId: number | null;
  setClientId: React.Dispatch<React.SetStateAction<number | null>>;
}

const operationsService = new OperationsService();
const projectService = new ProjectService();
const CLIENT_SELECT_ERROR_TEXT = "Please select a Client";
const PROJECT_SELECT_ERROR_TEXT = "Cannot Run Succeeded or NOT RUN Projects";
const projectStatusesEnum = [
  ProjectRunConfigurationStatus.Unknown,
  ProjectRunConfigurationStatus.Created,
  ProjectRunConfigurationStatus.Queued,
  ProjectRunConfigurationStatus.Analyzing,
  ProjectRunConfigurationStatus.Aggregating,
  ProjectRunConfigurationStatus.Succeeded,
  ProjectRunConfigurationStatus.Failed,
  ProjectRunConfigurationStatus.Canceled
];

const statusFilterItems: DropdownItem<string>[] = projectStatusesEnum.map((status) => {
  const displayValue = status.toUpperCase() !== "UNKNOWN" ? status.toUpperCase() : "NOT RUN";
  const key = displayValue === "NOT RUN" ? "UNKNOWN" : displayValue;
  return { key, name: displayValue };
});

const ALL_LINES_OF_BUSINESS = "all";
const OTHER_LINES_OF_BUSINESS = "other";

const projectStatusColumns: ColumnConfig = {
  /* oxlint-disable sort-keys */
  projectId: {
    displayText: "Project Id",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__project-id",
  },
  projectName: {
    displayText: "Project Name",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__project-name",
  },
  templateName: {
    displayText: "Template Name",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__template-name",
  },
  primaryRunConfigCreationDate: {
    displayText: "Primary Run Configuration Creation Date",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__primary-run-config-create-date",
  },
  primaryRunConfigStatus: {
    displayText: "Primary Run Configuration Status",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__primary-run-config-status",
  },
  secondaryRunConfigCreationDate: {
    displayText: "Secondary Run Configuration Creation Date",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__secondary-run-config-create-date",
  },
  secondaryRunConfigStatus: {
    displayText: "Secondary Run Configuration Status",
    sizeContentToFit: true,
    testId: "weekend-run-monitoring__secondary-run-config",
  }
  /* oxlint-enable sort-keys */
};

const WeekendRunProjectStatuses: React.FC<WeekendRunProjectStatusesProps> = ({ clients, clientId, setClientId }) => {
  const isHumana = clients.find((c) => c.clientId === clientId)?.name === "Humana";
  const isUHC = clients.find((c) => c.clientId === clientId)?.name === "United HealthCare Services";
  const [weekendRunStats, setWeekendRunStats] = useState<WeekendRunProjectStatusesResult[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [runDate, setRunDate] = useState<string>(isHumana ? getMostRecentSaturday() : getMostRecentWednesday());
  const [limitRunDate, setLimitRunDate] = useState<string>("");
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<string[]>([]);
  const [searchProjectString, setSearchProjectString] = useState<string>('');
  const [lineOfBusinessOptions, setLineOfBusinessOptions] = useState<string[]>([]);
  const [lineOfBusiness, setLineOfBusiness] = useState<string>(ALL_LINES_OF_BUSINESS);

  useEffect(() => {
    getProjectStatuses();
  }, []);

  useEffect(() => {
    resetFormValues();
    setLineOfBusiness(ALL_LINES_OF_BUSINESS);
    setLineOfBusinessOptions([]);
    if (clientId == null || !isUHC) {
      return;
    }
    operationsService
      .getLinesOfBusiness(clientId, true)
      .then(linesOfBusiness => setLineOfBusinessOptions(linesOfBusiness))
      .catch(() => setError("Failed to load lines of business."));
  }, [clientId, isUHC]);

  const resetFormValues = () => {
    setError(null);
    setWeekendRunStats(null);
    setSelectedProjects([]);
  };

  const getProjectStatuses = async () => {
    resetFormValues();
    setLoading(true);
    setError(null);

    try {
      if (!!clientId && !!runDate) {
        const adjustedLineOfBusiness = lineOfBusiness === ALL_LINES_OF_BUSINESS ? "" : lineOfBusiness;
        const response = await operationsService.getProjectStatusesByClient(
          clientId,
          runDate,
          adjustedLineOfBusiness,
          limitRunDate
        );
        const stats = selectedStatusFilters.length > 0 ? response.filter(item => (selectedStatusFilters.includes(item.primaryRunConfigStatus.toUpperCase()) || selectedStatusFilters.includes(item.secondaryRunConfigStatus.toUpperCase()))) : response;
        const filteredProjectName = searchProjectString ? stats.filter(item => (item.projectName.toLowerCase().includes(searchProjectString.toLowerCase()))) : stats;
        setWeekendRunStats(filteredProjectName);
      }
    } catch (error) {
      setError("No Projects For Given Date");
    } finally {
      setLoading(false);
    }
  };

  const requeueProjectButtonHandler = async () => {
    try {
      setLoading(true);
      setWeekendRunStats(null);
      for (const id of selectedProjects) {
        const idIndex = Number(id);
        const selectedProject = weekendRunStats[idIndex];
        await projectService.executeForClient(isHumana, isUHC, selectedProject.projectId);
      }

      toast("Project(s) Requeued",
        {
          containerId: "z-index-9999",
          type: "success",
        }
      )

    }
    catch (error) {
      setError("Error: Failed to Resubmit Project");
      toast("Project(s) Failed to Requeue",
        {
          containerId: "z-index-9999",
          type: "error",
        }
      )
    }
    finally {
      setSelectedProjects([]);
      setLoading(false);
      getProjectStatuses();
    }
  }

  function checkBoxDisabled(runStatus: string, secondaryRunStatus: string, primaryJobs: number, secondaryJobs: number) {
    if (runStatus === ProjectRunConfigurationStatus.Unknown || runStatus === ProjectRunConfigurationStatus.Succeeded || runStatus === ProjectRunConfigurationStatus.Queued) {
      return true;
    }
    else if (runStatus === ProjectRunConfigurationStatus.Analyzing || runStatus === ProjectRunConfigurationStatus.Aggregating || secondaryRunStatus === ProjectRunConfigurationStatus.Analyzing || secondaryRunStatus === ProjectRunConfigurationStatus.Aggregating) {
      if (primaryJobs || secondaryJobs) {
        return true;
      }
    }
    return false;
  }

  const tableData = useMemo(() => {
    let data: WeekendRunProjectStatusesResult[] = [];
    if (weekendRunStats) {
      data = weekendRunStats.map((stat, index) => ({
        /* oxlint-disable sort-keys */
        key: index.toString(),
        projectId: stat.projectId,
        projectName: stat.projectName,
        templateName: stat.templateName,
        primaryRunConfigCreationDate: stat.primaryRunConfigCreationDate || "NOT RUN",
        primaryRunConfigStatus: stat.primaryRunConfigStatus === ProjectRunConfigurationStatus.Unknown ? "NOT RUN" : stat.primaryRunConfigStatus.toUpperCase(),
        secondaryRunConfigCreationDate: stat.secondaryMemberSetId ? (stat.secondaryRunConfigCreationDate || "NOT RUN") : "N/A",
        secondaryRunConfigStatus: stat.secondaryMemberSetId ? (stat.secondaryRunConfigStatus === ProjectRunConfigurationStatus.Unknown ? "NOT RUN" : stat.secondaryRunConfigStatus.toUpperCase()) : "N/A",
        secondaryMemberSetId: stat.secondaryMemberSetId,
        checkBoxIsDisabled: checkBoxDisabled(stat.primaryRunConfigStatus, stat.secondaryRunConfigStatus, stat.primaryRunConfigurationWithRemainingJobs, stat.secondaryRunConfigurationWithRemainingJobs),
        primaryRunConfigurationWithRemainingJobs: stat.primaryRunConfigurationWithRemainingJobs,
        secondaryRunConfigurationWithRemainingJobs: stat.secondaryRunConfigurationWithRemainingJobs
        /* oxlint-enable sort-keys */
      }));
    }
    return data;
  }, [weekendRunStats]);

  const handleSort = (records: WeekendRunProjectStatusesResult[], column: keyof WeekendRunProjectStatusesResult, direction: SortDirections) => {
    const data = [...records];
    if (direction === SortDirections.None) {
      return data;
    }

    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    switch (column) {
      case "primaryRunConfigCreationDate":
        return sortByDate(data, "primaryRunConfigCreationDate", direction, "primaryRunConfigStatus");
      case "secondaryRunConfigCreationDate":
        return sortByDate(data, "secondaryRunConfigCreationDate", direction, "primaryRunConfigStatus");
      default:
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
    }
  }

  const isClientSelectionInvalid = clientId === null;

  let calculateDisabledTooltipText = "";
  if (isClientSelectionInvalid)
    calculateDisabledTooltipText = CLIENT_SELECT_ERROR_TEXT;

  let calculateDisabledTooltipRequeueText = "";
  if (selectedProjects.length == 0 || weekendRunStats == null) {
    calculateDisabledTooltipRequeueText = PROJECT_SELECT_ERROR_TEXT;
  }

  return (
    <div>
      <div className="form-row mb-3">
        <div className="col pe-1">
          <QESSelect
            label="Client"
            id="clientSelect"
            value={clientId ?? ""}
            onChange={(event) => setClientId(Number(event.target.value))}
            placeholder={clients.length === 0 ? "Loading Clients..." : "Select Client"}
            testId="weekend-run-monitoring__client-select-uhc"
            required
            disabled={clients.length === 0}
            isValid={!isClientSelectionInvalid}
            errorText={CLIENT_SELECT_ERROR_TEXT}
            positionDescriptionTextAbsolutely={true}
          >
            {clients && clients.map((client) => (
              <option key={client.clientId} value={client.clientId.toString()}>
                {client.name}
              </option>
            ))}
          </QESSelect>
        </div>
        {isUHC && (
          <div className="col px-1">
            <QESSelect
              label="Line of Business"
              value={lineOfBusiness}
              onChange={(event) => setLineOfBusiness(event.target.value)}
              testId="weekend-run-monitoring__line-of-business-select-uhc"
              disabled={clientId === null}
            >
              <option value={ALL_LINES_OF_BUSINESS}>All</option>
              {lineOfBusinessOptions.map((lob) => (
                <option key={lob.toLowerCase()} value={lob.toLowerCase()}>
                  {lob.toUpperCase()}
                </option>
              ))}
              <option value={OTHER_LINES_OF_BUSINESS}>Other</option>
            </QESSelect>
          </div>
        )}
        <div className="col px-1">
          <QESDateInput
            label="Run Date"
            value={runDate}
            onChange={(event) => setRunDate(event.target.value)}
            testId="weekend-run-monitoring__run-date-input-uhc"
          />
        </div>
        <div className="col px-1">
          <QESDateInput
            label="Upper Limit Run Date"
            value={limitRunDate}
            onChange={(event) => setLimitRunDate(event.target.value)}
            testId="weekend-run-monitoring__limit-run-date-input-uhc"
          />
        </div>
        <div className="col px-1">
          <MultiselectDropdown
            items={statusFilterItems}
            label="Project Status Filter"
            onSelectionChanged={(selectedItems) => setSelectedStatusFilters(selectedItems.map((item) => item.key))}
            selectedItems={statusFilterItems.filter((item) => selectedStatusFilters.includes(item.key))}
            testId="weekend-run-monitoring__project-status-filter-uhc"
          />
        </div>
        <div className="col px-1">
          <QESTextInput label="Project Name Filter" testId="weekend-run-monitoring-filter__project-name" value={searchProjectString} onChange={(event) => setSearchProjectString(event.target.value)} />
        </div>
        <div className="col d-flex align-items-end ps-1">
          <TooltipWrapper position="top" text={calculateDisabledTooltipText}>
            <QESButton
              onClick={getProjectStatuses}
              qesType="primary"
              qesTestId="weekly-run-monitoring__calculate-weekly-job-statistics-uhc"
            >
              Search
            </QESButton>
          </TooltipWrapper>
        </div>
      </div>
      <div>
        <TooltipWrapper position="top" text={calculateDisabledTooltipRequeueText}>
          <QESButton
            disabled={selectedProjects.length == 0 || weekendRunStats == null}
            onClick={requeueProjectButtonHandler}
            className="ms-2 me-1"
            qesTestId={"jobs-list__requeue-job-button"}>Requeue Project(s)</QESButton>
        </TooltipWrapper>
      </div>
      <div className="mt-4">
        {loading && <LoadingSpinner />}
        {error && <div>{error}</div>}
        {weekendRunStats && (
          <Waffles
            columns={projectStatusColumns}
            customSort={handleSort}
            data={tableData}
            enablePagination={false}
            testId="weekend-run-monitoring__project-statistics-table-uhc"
            showCheckboxes={true}
            handleCheckboxChange={setSelectedProjects}
            selectedKeys={selectedProjects}
          />
        )}
      </div>
    </div>
  );
};

export default WeekendRunProjectStatuses;